import { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SyncIcon from '@mui/icons-material/Sync';
import ListAltIcon from '@mui/icons-material/ListAlt';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import { BreadcrumbHeader, FilterTabs, EmptyState, ActionMenu, ConfirmModal } from '../../components/shared';
import '../../styles/design-tokens.css';
import { fieldSx } from '../../theme/fieldStyles';

// ─── Constants ─────────────────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";
const TABS = [
{ label: 'All', value: 'all' },
{ label: 'Connected', value: 'connected' },
{ label: 'Disconnected', value: 'disconnected' }];

const CATEGORY_COLORS = {
  Ticketing: { bg: '#EDE7F6', color: '#4527A0' },
  Carrier: { bg: '#E3F2FD', color: '#1565C0' },
  Accounting: { bg: '#E8F5E9', color: '#2E7D32' },
  Contracts: { bg: '#FCE4EC', color: '#880E4F' },
  Locations: { bg: '#FBE9E7', color: '#BF360C' },
  Notifications: { bg: '#E3F2FD', color: '#1565C0' }
};

const CONN_COLORS = {
  Connected: { bg: '#E8F5E9', color: '#2E7D32' },
  Disconnected: { bg: '#ECEFF1', color: '#546E7A' },
  Error: { bg: '#FFEBEE', color: '#B71C1C' }
};

const CONNECT_FIELDS = {
  servicehub: [
  { key: 'apiUrl', label: 'API URL', type: 'text' },
  { key: 'apiKey', label: 'API Key', type: 'password' },
  { key: 'syncInterval', label: 'Sync Interval', type: 'select',
    options: ['Every 15 min', 'Every 30 min', 'Every hour', 'Manual'], defaultValue: 'Every 15 min' }],

  contracthub: [
  { key: 'apiUrl', label: 'API URL', type: 'text' },
  { key: 'apiKey', label: 'API Key', type: 'password' },
  { key: 'syncInterval', label: 'Sync Interval', type: 'select',
    options: ['Every 15 min', 'Every 30 min', 'Every hour', 'Manual'], defaultValue: 'Every 15 min' },
  { key: 'syncContractTypes', label: 'Sync Contract Types', type: 'multicheck',
    options: ['Warranty', 'Service Contract', 'Entitlement'], defaultValue: ['Warranty', 'Service Contract', 'Entitlement'] }],

  fedex: [
  { key: 'accountNumber', label: 'Account Number', type: 'text' },
  { key: 'apiKey', label: 'API Key', type: 'password' },
  { key: 'apiSecret', label: 'API Secret', type: 'password' },
  { key: 'testMode', label: 'Test Mode', type: 'toggle', defaultValue: true }],

  shippo: [
  { key: 'apiKey', label: 'API Key', type: 'password' },
  { key: 'testMode', label: 'Test Mode', type: 'toggle', defaultValue: true },
  { key: 'carriers', label: 'Enabled Carriers', type: 'multicheck',
    options: ['FedEx', 'UPS', 'USPS', 'DHL Express', 'OnTrac'], defaultValue: ['FedEx', 'UPS', 'USPS'] }],

  quickbooks: [
  { key: 'companyId', label: 'Company ID', type: 'text' },
  { key: 'clientId', label: 'Client ID', type: 'text' },
  { key: 'clientSecret', label: 'Client Secret', type: 'password' },
  { key: 'environment', label: 'Environment', type: 'select',
    options: ['Sandbox', 'Production'], defaultValue: 'Sandbox' }],

  teams: [
  { key: 'workflowUrl', label: 'Workflow URL', type: 'password', placeholder: 'https://prod-xx.westus.logic.azure.com/...', helperText: 'Generate this URL from Power Automate in your Teams admin portal' },
  { key: 'defaultChannel', label: 'Default Channel Name', type: 'text', optional: true, placeholder: 'e.g. IT Alerts' },
  { key: 'notifyOn', label: 'Notify on', type: 'multicheck',
    options: ['SLA Breach', 'Low Stock', 'Shipment Delay', 'RMA Created', 'Vendor Order Initiated'],
    defaultValue: ['SLA Breach', 'Low Stock', 'Shipment Delay', 'RMA Created', 'Vendor Order Initiated'] },
  { key: 'messageFormat', label: 'Message Format', type: 'select',
    options: ['Simple (plain text)', 'Detailed (includes part #, customer, ticket #)'], defaultValue: 'Simple (plain text)' }]

};

const SIMULATED_VALUES = {
  servicehub: { apiUrl: 'https://api.servicehub.coverit.com', apiKey: '••••••••••', syncInterval: 'Every 15 min' },
  contracthub: { apiUrl: 'https://api.contracthub.coverit.com', apiKey: '••••••••••', syncInterval: 'Every 15 min', syncContractTypes: ['Warranty', 'Service Contract', 'Entitlement'] },
  fedex: { accountNumber: '7489120054', apiKey: '••••••••••', apiSecret: '••••••••••', testMode: false },
  quickbooks: { companyId: '1234567890', clientId: 'AB1C2D3E4F', clientSecret: '••••••••••', environment: 'Sandbox' },
  teams: { workflowUrl: '••••••••••', defaultChannel: 'IT Alerts', notifyOn: ['SLA Breach', 'Low Stock', 'Shipment Delay', 'RMA Created', 'Vendor Order Initiated'], messageFormat: 'Simple (plain text)' }
};

const INITIAL_INTEGRATIONS = [
{ id: 'servicehub', name: 'ServiceHub', category: 'Ticketing', description: 'Pulls part order tickets and customer data from ServiceHub into the system.', status: 'Connected', lastSynced: dayjs().subtract(3, 'minute').toISOString() },
{ id: 'contracthub', name: 'ContractHub', category: 'Contracts', description: 'Pulls contract and entitlement data from ContractHub to validate part order coverage and SLA terms.', status: 'Connected', lastSynced: dayjs().subtract(12, 'minute').toISOString() },
{ id: 'fedex', name: 'FedEx', category: 'Carrier', description: 'Generates shipping labels and retrieves live tracking updates for FedEx shipments.', status: 'Connected', lastSynced: dayjs().subtract(1, 'hour').toISOString() },
{ id: 'shippo', name: 'Shippo', category: 'Carrier', description: 'Multi-carrier shipping platform for generating labels, comparing rates, and tracking shipments across FedEx, UPS, USPS, and more.', status: 'Disconnected', lastSynced: null },
{ id: 'quickbooks', name: 'QuickBooks', category: 'Accounting', description: 'Syncs purchase orders, supplier payments, and shipping costs to QuickBooks.', status: 'Connected', lastSynced: dayjs().subtract(2, 'day').toISOString() },
{ id: 'teams', name: 'Microsoft Teams', category: 'Notifications', description: 'Posts real-time alerts and notifications to configured Microsoft Teams channels via Power Automate workflows.', status: 'Connected', lastSynced: dayjs().subtract(8, 'minute').toISOString() }];

const SH_SYNC_LOG = [
{ time: '2026-03-24 09:15:00', msg: 'Synced 12 tickets, 3 new part orders created' },
{ time: '2026-03-24 08:15:00', msg: 'Synced 9 tickets, 1 new part order created' },
{ time: '2026-03-24 07:15:00', msg: 'Synced 14 tickets, 2 new part orders created' },
{ time: '2026-03-24 06:15:00', msg: 'Synced 7 tickets, 0 new part orders created' },
{ time: '2026-03-24 05:15:00', msg: 'Synced 11 tickets, 4 new part orders created' },
{ time: '2026-03-23 23:15:00', msg: 'Synced 3 tickets, 0 new part orders created' },
{ time: '2026-03-23 22:15:00', msg: 'Synced 5 tickets, 1 new part order created' },
{ time: '2026-03-23 21:15:00', msg: 'Synced 8 tickets, 2 new part orders created' },
{ time: '2026-03-23 20:15:00', msg: 'Synced 6 tickets, 0 new part orders created' },
{ time: '2026-03-23 19:15:00', msg: 'Synced 10 tickets, 3 new part orders created' }];

const CH_SYNC_LOG = [
{ time: '2026-03-24 09:10:00', msg: 'Synced 24 contracts, 6 entitlements updated' },
{ time: '2026-03-24 08:10:00', msg: 'Synced 18 contracts, 2 entitlements updated' },
{ time: '2026-03-24 07:10:00', msg: 'Synced 31 contracts, 9 entitlements updated' },
{ time: '2026-03-24 06:10:00', msg: 'Synced 11 contracts, 0 entitlements updated' },
{ time: '2026-03-24 05:10:00', msg: 'Synced 27 contracts, 4 entitlements updated' },
{ time: '2026-03-23 23:10:00', msg: 'Synced 8 contracts, 1 entitlement updated' },
{ time: '2026-03-23 22:10:00', msg: 'Synced 14 contracts, 3 entitlements updated' },
{ time: '2026-03-23 21:10:00', msg: 'Synced 22 contracts, 5 entitlements updated' },
{ time: '2026-03-23 20:10:00', msg: 'Synced 16 contracts, 2 entitlements updated' },
{ time: '2026-03-23 19:10:00', msg: 'Synced 20 contracts, 7 entitlements updated' }];

const SH_STATS = [
{ label: 'Open Tickets Synced', value: '147' },
{ label: 'Parts Orders Created Today', value: '8' },
{ label: 'Last Sync Duration', value: '2.3s' }];

const CH_STATS = [
{ label: 'Contracts Synced', value: '312' },
{ label: 'Active Entitlements', value: '89' },
{ label: 'Last Sync Duration', value: '1.8s' }];

// ─── Helpers ───────────────────────────────────────────────────────────────────

function syncedAgo(iso) {
  if (!iso) return null;
  const mins = dayjs().diff(dayjs(iso), 'minute');
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  const hrs = dayjs().diff(dayjs(iso), 'hour');
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = dayjs().diff(dayjs(iso), 'day');
  return `${days} day${days !== 1 ? 's' : ''} ago`;
}

function buildDefaultForm(fields) {
  const form = {};
  for (const f of fields) {
    if (f.type === 'toggle') form[f.key] = f.defaultValue ?? false;else
    if (f.type === 'select') form[f.key] = f.defaultValue ?? f.options?.[0] ?? '';else
    if (f.type === 'multicheck') form[f.key] = f.defaultValue ?? [];else
    form[f.key] = '';
  }
  return form;
}

// ─── CategoryBadge ─────────────────────────────────────────────────────────────

function CategoryBadge({ category }) {
  const cfg = CATEGORY_COLORS[category] ?? { bg: '#ECEFF1', color: '#546E7A' };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '2px', borderRadius: '10px', backgroundColor: cfg.bg }}>
      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: cfg.color, lineHeight: 1, whiteSpace: 'nowrap' }}>
        {category}
      </Typography>
    </Box>);

}

// ─── ConnectionBadge ───────────────────────────────────────────────────────────

function ConnectionBadge({ status }) {
  const cfg = CONN_COLORS[status] ?? CONN_COLORS.Disconnected;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '10px', py: '3px', borderRadius: '12px', backgroundColor: cfg.bg }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: cfg.color, lineHeight: 1, whiteSpace: 'nowrap' }}>
        {status}
      </Typography>
    </Box>);

}

// ─── IntegrationCard ───────────────────────────────────────────────────────────

function IntegrationCard({ integration, testing, onConnect, onConfigure, onTestConnection, onSyncNow, onDisconnect, onViewError }) {
  const { name, category, description, status, lastSynced } = integration;
  const ago = syncedAgo(lastSynced);
  const isConnected = status === 'Connected';
  const isError = status === 'Error';

  const actions = [
  ...(isConnected ? [
  { label: 'Configure', onClick: onConfigure },
  { label: 'Test Connection', onClick: onTestConnection },
  { label: 'Sync Now', onClick: onSyncNow },
  { label: 'Disconnect', onClick: onDisconnect }] :
  []),
  ...(!isConnected && !isError ? [
  { label: 'Connect', onClick: onConnect }] :
  []),
  ...(isError ? [
  { label: 'View Error Details', onClick: onViewError }] :
  [])];

  return (
    <ContentCard
      sx={{ p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5
      }}>
      
      {/* Top row: name + category | status badge + menu */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)', lineHeight: 1.3, mb: '6px' }}>
            {name}
          </Typography>
          <CategoryBadge category={category} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          <ConnectionBadge status={status} />
          <ActionMenu actions={actions} />
        </Box>
      </Box>

      {/* Description */}
      <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', lineHeight: 1.5 }}>
        {description}
      </Typography>

      {/* Last synced */}
      <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)' }}>
        {ago ? `Last synced: ${ago}` : 'Never synced'}
      </Typography>

      {/* Primary action */}
      <Box sx={{ mt: 'auto', pt: '4px' }}>
        {testing ?
        <Button
          variant="outlined"
          size="small"
          disabled
          startIcon={<CircularProgress size={13} sx={{ color: 'var(--grey-400)' }} />}
          sx={{ textTransform: 'none', fontSize: '0.875rem', borderColor: 'var(--grey-300)', color: 'var(--grey-400)' }}>
          
            Testing…
          </Button> :
        isConnected ?
        <Button
          variant="outlined"
          size="small"
          onClick={onConfigure}
          sx={{ textTransform: 'none', fontSize: '0.875rem', fontWeight: 600,
            borderColor: 'var(--grey-300)', color: 'var(--grey-700)',
            '&:hover': { borderColor: '#009688', color: '#009688', backgroundColor: 'transparent' }
          }}>
          
            Configure
          </Button> :
        isError ?
        <Button
          variant="contained"
          size="small"
          onClick={onConnect}
          sx={{ textTransform: 'none', fontSize: '0.875rem', fontWeight: 600, boxShadow: 'none',
            backgroundColor: '#E65100', '&:hover': { backgroundColor: '#BF360C' }
          }}>
          
            Reconnect
          </Button> :

        <Button
          variant="contained"
          size="small"
          onClick={onConnect}
          sx={{ textTransform: 'none', fontSize: '0.875rem', fontWeight: 600, boxShadow: 'none',
            backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' }
          }}>
          
            Connect
          </Button>
        }
      </Box>
    </ContentCard>);

}

// ─── ConnectModal ──────────────────────────────────────────────────────────────

function ConnectModal({ open, mode, integration, onClose, onSave }) {
  const [form, setForm] = useState({});
  const [showPass, setShowPass] = useState({});
  const [testMsgBanner, setTestMsgBanner] = useState('');
  const isTeams = integration?.id === 'teams';

  const fields = integration ? CONNECT_FIELDS[integration.id] ?? [] : [];

  const handleEnter = () => {
    const defaults = buildDefaultForm(fields);
    if (mode === 'configure' && SIMULATED_VALUES[integration?.id]) {
      setForm({ ...defaults, ...SIMULATED_VALUES[integration.id] });
    } else {
      setForm(defaults);
    }
    setShowPass({});
    setTestMsgBanner('');
  };

  const handleTeamsTestMessage = () => {
    setTestMsgBanner('Test message sent to Teams');
    setTimeout(() => setTestMsgBanner(''), 3000);
  };

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));
  const toggleShow = (key) => setShowPass((prev) => ({ ...prev, [key]: !prev[key] }));

  const isValid = fields.every((f) => {
    if (f.type === 'toggle' || f.type === 'select' || f.type === 'multicheck') return true;
    if (f.optional) return true;
    return (form[f.key] ?? '').trim() !== '';
  });

  const renderField = (f) => {
    if (f.type === 'toggle') {
      return (
        <FormControlLabel
          key={f.key}
          control={
          <Switch
            checked={!!form[f.key]}
            onChange={(e) => setField(f.key, e.target.checked)}
            size="small"
            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#009688' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#009688' }
            }} />

          }
          label={<Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>{f.label}</Typography>} />);

    }

    if (f.type === 'multicheck') {
      const selected = Array.isArray(form[f.key]) ? form[f.key] : [];
      const toggle = (opt) => setField(f.key, selected.includes(opt) ?
      selected.filter((o) => o !== opt) :
      [...selected, opt]
      );
      return (
        <Box key={f.key}>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-700)', mb: '6px' }}>
            {f.label}
          </Typography>
          <FormGroup>
            {f.options.map((opt) =>
            <FormControlLabel
              key={opt}
              control={
              <Checkbox
                size="small"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
                sx={{ color: 'var(--grey-400)', '&.Mui-checked': { color: '#009688' }, p: '4px' }} />

              }
              label={<Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>{opt}</Typography>} />

            )}
          </FormGroup>
        </Box>);

    }

    if (f.type === 'select') {
      return (
        <FormControl key={f.key} size="small" fullWidth sx={fieldSx}>
          <InputLabel>{f.label}</InputLabel>
          <Select
            label={f.label}
            value={form[f.key] ?? ''}
            onChange={(e) => setField(f.key, e.target.value)}
            sx={{ fontSize: '0.875rem' }}>
            
            {f.options.map((opt) =>
            <MenuItem key={opt} value={opt} sx={{ fontSize: '0.875rem' }}>{opt}</MenuItem>
            )}
          </Select>
        </FormControl>);

    }

    const isPass = f.type === 'password';
    return (
      <TextField
        key={f.key}
        size="small"
        fullWidth
        label={f.label}
        type={isPass && !showPass[f.key] ? 'password' : f.type === 'email' ? 'email' : 'text'}
        value={form[f.key] ?? ''}
        onChange={(e) => setField(f.key, e.target.value)}
        placeholder={f.placeholder ?? ''}
        helperText={f.helperText}
        FormHelperTextProps={f.helperText ? { sx: { fontSize: '0.75rem', color: 'var(--grey-500)', mx: 0, mt: '4px' } } : undefined}
        sx={fieldSx}
        InputProps={isPass ? {
          endAdornment:
          <InputAdornment position="end">
              <IconButton
              size="small"
              onClick={() => toggleShow(f.key)}
              edge="end"
              sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-800)' } }}>
              
                {showPass[f.key] ?
              <VisibilityOff sx={{ fontSize: 18 }} /> :
              <Visibility sx={{ fontSize: 18 }} />
              }
              </IconButton>
            </InputAdornment>

        } : undefined} />);

  };

  return (
    <Dialog open={open} onClose={(e, reason) => {if (reason !== 'backdropClick') onClose();}} maxWidth="sm" fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {mode === 'connect' ? `Connect ${integration?.name ?? ''}` : `Configure ${integration?.name ?? ''}`}
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        {fields.map((f) => renderField(f))}

        {/* Teams: Test Message button */}
        {isTeams &&
        <Box>
            {testMsgBanner &&
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: '6px', px: 2, py: '10px', mb: 1 }}>
                <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 16 }} />
                <Typography sx={{ fontSize: '0.8125rem', color: '#2E7D32', fontWeight: 600 }}>{testMsgBanner}</Typography>
              </Box>
          }
            <Button
            variant="outlined"
            size="small"
            onClick={handleTeamsTestMessage}
            sx={{ textTransform: 'none', fontSize: '0.875rem',
              borderColor: 'var(--grey-300)', color: 'var(--grey-700)',
              '&:hover': { borderColor: '#009688', color: '#009688', backgroundColor: 'transparent' }
            }}>
            
              Test Message
            </Button>
          </Box>
        }
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          
          Cancel
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={!isValid}
          onClick={() => onSave(form)}
          sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2
          }}>
          
          {mode === 'connect' ? 'Connect' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── ErrorDetailsModal ─────────────────────────────────────────────────────────

function ErrorDetailsModal({ open, integration, onClose, onReconnect }) {
  return (
    <Dialog open={open} onClose={(e, reason) => {if (reason !== 'backdropClick') onClose();}} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Error Details — {integration?.name}
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box
          sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start',
            backgroundColor: '#FFEBEE', border: '1px solid #FFCDD2',
            borderRadius: '6px', px: 2, py: 1.5
          }}>
          
          <ErrorOutlineIcon sx={{ color: '#B71C1C', fontSize: 18, mt: '1px', flexShrink: 0 }} />
          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#B71C1C', mb: '4px' }}>
              Connection Failed
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: '#B71C1C' }}>
              Authentication error: API key may have expired or been revoked. Please reconnect with valid credentials.
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)' }}>
          Last error occurred: {syncedAgo(integration?.lastSynced) ?? 'Unknown'}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          
          Close
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={onReconnect}
          sx={{ backgroundColor: '#E65100', '&:hover': { backgroundColor: '#BF360C' },
            textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2
          }}>
          
          Reconnect
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── SyncLogModal ──────────────────────────────────────────────────────────────

function SyncLogModal({ open, title, log, onClose }) {
  return (
    <Dialog open={open} onClose={(e, reason) => {if (reason !== 'backdropClick') onClose();}} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {title}
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {(log ?? []).map((entry, i) =>
        <Box
          key={i}
          sx={{ display: 'flex', gap: 2, px: 3, py: '10px',
            borderBottom: i < (log ?? []).length - 1 ? '1px solid var(--grey-100)' : 'none'
          }}>
          
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', fontFamily: 'monospace', flexShrink: 0 }}>
              {entry.time}
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-800)' }}>
              {entry.msg}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onClose}
          sx={{ textTransform: 'none', fontSize: '0.875rem',
            borderColor: 'var(--grey-300)', color: 'var(--grey-700)',
            '&:hover': { borderColor: '#009688', color: '#009688', backgroundColor: 'transparent' }
          }}>
          
          Close
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── ServiceHubSyncSection ─────────────────────────────────────────────────────

function ServiceHubSyncSection({ integration, onSyncNow, onViewLog }) {
  const ago = syncedAgo(integration.lastSynced);
  return (
    <ContentCard sx={{ overflow: 'hidden' }}>
      <Box
        sx={{ px: 3, py: 2,
          borderBottom: '1px solid var(--grey-200)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1
        }}>
        
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
          ServiceHub Sync Status
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ConnectionBadge status={integration.status} />
          {ago &&
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)' }}>
              Last synced: {ago}
            </Typography>
          }
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 2.5 }}>
        {/* Stats rows */}
        <Box sx={{ mb: 2.5 }}>
          {SH_STATS.map((row, i) =>
          <Box
            key={row.label}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              py: '10px',
              borderBottom: i < SH_STATS.length - 1 ? '1px solid var(--grey-100)' : 'none'
            }}>
            
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-600)' }}>{row.label}</Typography>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-900)', fontWeight: 600 }}>{row.value}</Typography>
            </Box>
          )}
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SyncIcon sx={{ fontSize: 16 }} />}
            onClick={onSyncNow}
            sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
              textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none'
            }}>
            
            Sync Now
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ListAltIcon sx={{ fontSize: 16 }} />}
            onClick={onViewLog}
            sx={{ textTransform: 'none', fontSize: '0.875rem',
              borderColor: 'var(--grey-300)', color: 'var(--grey-700)',
              '&:hover': { borderColor: '#009688', color: '#009688', backgroundColor: 'transparent' }
            }}>
            
            View Sync Log
          </Button>
        </Box>
      </Box>
    </ContentCard>);

}

// ─── ContractHubSyncSection ────────────────────────────────────────────────────

function ContractHubSyncSection({ integration, onSyncNow, onViewLog }) {
  const ago = syncedAgo(integration.lastSynced);
  return (
    <ContentCard sx={{ overflow: 'hidden' }}>
      <Box
        sx={{ px: 3, py: 2,
          borderBottom: '1px solid var(--grey-200)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1
        }}>
        
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
          ContractHub Sync Status
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ConnectionBadge status={integration.status} />
          {ago &&
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)' }}>
              Last synced: {ago}
            </Typography>
          }
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 2.5 }}>
        <Box sx={{ mb: 2.5 }}>
          {CH_STATS.map((row, i) =>
          <Box
            key={row.label}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              py: '10px',
              borderBottom: i < CH_STATS.length - 1 ? '1px solid var(--grey-100)' : 'none'
            }}>
            
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-600)' }}>{row.label}</Typography>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-900)', fontWeight: 600 }}>{row.value}</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SyncIcon sx={{ fontSize: 16 }} />}
            onClick={onSyncNow}
            sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
              textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none'
            }}>
            
            Sync Now
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ListAltIcon sx={{ fontSize: 16 }} />}
            onClick={onViewLog}
            sx={{ textTransform: 'none', fontSize: '0.875rem',
              borderColor: 'var(--grey-300)', color: 'var(--grey-700)',
              '&:hover': { borderColor: '#009688', color: '#009688', backgroundColor: 'transparent' }
            }}>
            
            View Sync Log
          </Button>
        </Box>
      </Box>
    </ContentCard>);

}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function IntegrationsPage() {
  const [integrations, setIntegrations] = useState(INITIAL_INTEGRATIONS);
  const [activeTab, setActiveTab] = useState('all');
  const [banner, setBanner] = useState(null); // { msg, type: 'success'|'warning' }
  const [testingId, setTestingId] = useState(null);

  // Modal state
  const [modalState, setModalState] = useState({ open: false, mode: 'connect', id: null });
  const [confirmState, setConfirmState] = useState({ open: false, id: null });
  const [errorModalId, setErrorModalId] = useState(null);
  const [syncLogModal, setSyncLogModal] = useState(null); // null | 'servicehub' | 'contracthub'

  const showBanner = (msg, type = 'success') => {
    setBanner({ msg, type });
    setTimeout(() => setBanner(null), 4000);
  };

  const getIntegration = (id) => integrations.find((i) => i.id === id);

  const updateIntegration = (id, changes) =>
  setIntegrations((prev) => prev.map((i) => i.id === id ? { ...i, ...changes } : i));

  const filtered = useMemo(() => integrations.filter((i) => {
    if (activeTab === 'connected') return i.status === 'Connected';
    if (activeTab === 'disconnected') return i.status !== 'Connected';
    return true;
  }), [integrations, activeTab]);

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const handleConnect = (id) => {
    const name = getIntegration(id)?.name ?? id;
    updateIntegration(id, { status: 'Connected', lastSynced: dayjs().toISOString() });
    setModalState({ open: false, mode: 'connect', id: null });
    showBanner(`${name} connected successfully.`);
  };

  const handleConfigure = (id) => {
    const name = getIntegration(id)?.name ?? id;
    setModalState({ open: false, mode: 'configure', id: null });
    showBanner(`${name} settings updated successfully.`);
  };

  const handleDisconnectConfirm = () => {
    const id = confirmState.id;
    const name = getIntegration(id)?.name ?? id;
    updateIntegration(id, { status: 'Disconnected', lastSynced: null });
    setConfirmState({ open: false, id: null });
    showBanner(`Disconnected from ${name}.`, 'warning');
  };

  const handleTestConnection = (id) => {
    const name = getIntegration(id)?.name ?? id;
    setTestingId(id);
    setTimeout(() => {
      setTestingId(null);
      showBanner(`Connection to ${name} is working.`);
    }, 1500);
  };

  const handleSyncNow = (id) => {
    const name = getIntegration(id)?.name ?? id;
    updateIntegration(id, { lastSynced: dayjs().toISOString() });
    showBanner(`Sync started for ${name}.`);
  };

  const openConnect = (id) => setModalState({ open: true, mode: 'connect', id });
  const openConfigure = (id) => setModalState({ open: true, mode: 'configure', id });
  const openDisconnect = (id) => setConfirmState({ open: true, id });

  const modalIntegration = modalState.id ? getIntegration(modalState.id) : null;
  const confirmName = confirmState.id ? getIntegration(confirmState.id)?.name ?? '' : '';
  const shIntegration = integrations.find((i) => i.id === 'servicehub');
  const chIntegration = integrations.find((i) => i.id === 'contracthub');

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Banner */}
      {banner &&
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: banner.type === 'warning' ? '#FFF8E1' : '#E8F5E9',
          border: `1px solid ${banner.type === 'warning' ? '#FFE082' : '#A5D6A7'}`,
          borderRadius: '8px', px: 2, py: '12px'
        }}>
        
          {banner.type === 'warning' ?
        <WarningAmberIcon sx={{ color: '#E65100', fontSize: 20 }} /> :
        <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
        }
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: banner.type === 'warning' ? '#E65100' : '#2E7D32' }}>
            {banner.msg}
          </Typography>
        </Box>
      }

      <BreadcrumbHeader
        title="Integrations"
        crumbs={[{ label: 'Settings' }, { label: 'Integrations' }]} />

      <ContentCard sx={{ px: 2, '& > div': { mb: 0 } }}>
        <FilterTabs tabs={TABS} value={activeTab} onChange={setActiveTab} />
      </ContentCard>

      {/* Cards grid */}
      {filtered.length === 0 ?
      <ContentCard sx={{ overflow: 'hidden' }}>
          <EmptyState title="No integrations found" description="Try adjusting your filter." />
        </ContentCard> :

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {filtered.map((integration) =>
        <IntegrationCard
          key={integration.id}
          integration={integration}
          testing={testingId === integration.id}
          onConnect={() => openConnect(integration.id)}
          onConfigure={() => openConfigure(integration.id)}
          onTestConnection={() => handleTestConnection(integration.id)}
          onSyncNow={() => handleSyncNow(integration.id)}
          onDisconnect={() => openDisconnect(integration.id)}
          onViewError={() => setErrorModalId(integration.id)} />

        )}
        </Box>
      }

      {/* ServiceHub Sync Status — only when connected */}
      {shIntegration?.status === 'Connected' &&
      <ServiceHubSyncSection
        integration={shIntegration}
        onSyncNow={() => handleSyncNow('servicehub')}
        onViewLog={() => setSyncLogModal('servicehub')} />

      }

      {/* ContractHub Sync Status — only when connected */}
      {chIntegration?.status === 'Connected' &&
      <ContractHubSyncSection
        integration={chIntegration}
        onSyncNow={() => handleSyncNow('contracthub')}
        onViewLog={() => setSyncLogModal('contracthub')} />

      }

      {/* Connect / Configure Modal */}
      <ConnectModal
        open={modalState.open}
        mode={modalState.mode}
        integration={modalIntegration}
        onClose={() => setModalState((prev) => ({ ...prev, open: false }))}
        onSave={() =>
        modalState.mode === 'connect' ?
        handleConnect(modalState.id) :
        handleConfigure(modalState.id)
        } />

      {/* Error Details Modal */}
      <ErrorDetailsModal
        open={!!errorModalId}
        integration={errorModalId ? getIntegration(errorModalId) : null}
        onClose={() => setErrorModalId(null)}
        onReconnect={() => {
          const id = errorModalId;
          setErrorModalId(null);
          openConnect(id);
        }} />

      {/* Disconnect Confirmation */}
      <ConfirmModal
        open={confirmState.open}
        title={`Disconnect ${confirmName}?`}
        message={`Are you sure you want to disconnect ${confirmName}? All sync settings will be lost.`}
        confirmLabel="Disconnect"
        onConfirm={handleDisconnectConfirm}
        onCancel={() => setConfirmState({ open: false, id: null })} />

      {/* Sync Log Modal */}
      <SyncLogModal
        open={!!syncLogModal}
        title={syncLogModal === 'contracthub' ? 'ContractHub Sync Log' : 'ServiceHub Sync Log'}
        log={syncLogModal === 'contracthub' ? CH_SYNC_LOG : SH_SYNC_LOG}
        onClose={() => setSyncLogModal(null)} />
      
    </Box>);

}